import React from 'react';
import { useAppStateContext } from 'src/AppState/AppState';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { RouterBackoffice } from 'src/AppState/RouterBackoffice';
import { MenuButtonWrapperCms, MenuItemsNavigationCms } from 'src/components/CmsPageNavigation/CmsPageNavigation.style';
import { observer } from 'src/utils/mobx-react';

export type CmsRouteType =
    | 'casinoBanners'
    | 'eventDetails'
    | 'footerManagement'
    | 'global'
    | 'headerLinkPanel'
    | 'headlineCarousel'
    | 'heroWidget'
    | 'homePageCarousel'
    | 'landingPage'
    | 'liveNotificationsPage'
    | 'notificationPage'
    | 'pages'
    | 'promoCode'
    | 'promotedEvents'
    | 'topEvents'
    | 'quickLinkTray'
    | 'specialSports'
    | 'sports'
    | 'translations'
    | 'welcomeBonus'
    | 'seoManagement';

type MenuItemsType = {
    dataTest: string;
    id: number;
    label: string;
    onClick: () => void;
    routeParam: CmsRouteType;
};

const renderLhsMenuItems = (configState: ConfigState, routerBackoffice: RouterBackoffice): MenuItemsType[] => {
    const cmsHiddenTabsLHS = configState.config.cmsHiddenTabs;
    const topEventsEnable = configState.config.topEvents;

    const menuItems: MenuItemsType[] = [
        {
            dataTest: 'global',
            id: 1,
            label: 'Global',
            onClick: (): void => routerBackoffice.redirectToCmsGlobal(),
            routeParam: 'global',
        },
        {
            dataTest: 'sports',
            id: 2,
            label: 'Sports',
            onClick: (): void => routerBackoffice.redirectToCmsSports(),
            routeParam: 'sports',
        },
        {
            dataTest: 'promoted-events',
            id: 3,
            label: 'Promoted Events',
            onClick: (): void => routerBackoffice.redirectToCmsPromotedEvents(),
            routeParam: 'promotedEvents',
        },
        {
            dataTest: 'landing-page',
            id: 5,
            label: 'Landing Page',
            onClick: (): void => routerBackoffice.redirectToCmsLandingPage(),
            routeParam: 'landingPage',
        },
        {
            dataTest: 'pages',
            id: 6,
            label: 'Pages',
            onClick: (): void => routerBackoffice.redirectToCmsPages(),
            routeParam: 'pages',
        },
        {
            dataTest: 'notification-page',
            id: 7,
            label: 'Promo',
            onClick: (): void => routerBackoffice.redirectToCmsNotificationPage(),
            routeParam: 'notificationPage',
        },
        {
            dataTest: 'promo-code',
            id: 8,
            label: 'Promo Code',
            onClick: (): void => routerBackoffice.redirectToCmsPromoCode(),
            routeParam: 'promoCode',
        },
        {
            dataTest: 'welcome-bonus',
            id: 9,
            label: 'Welcome Bonus',
            onClick: (): void => routerBackoffice.redirectToCmsWelcomeBonus(),
            routeParam: 'welcomeBonus',
        },
        {
            dataTest: 'live-notifications-page',
            id: 10,
            label: 'Live Notifications',
            onClick: (): void => routerBackoffice.redirectToCmsLiveNotificationsPage(),
            routeParam: 'liveNotificationsPage',
        },
        {
            dataTest: 'hero-widget',
            id: 11,
            label: 'Hero widget',
            onClick: (): void => routerBackoffice.redirectToCmsHeroWidget(),
            routeParam: 'heroWidget',
        },
        // This feature is currently not being used
        // {
        //     dataTest: 'event-details',
        //     id: 12,
        //     label: 'Hero Event Details',
        //     onClick: (): void => routerBackoffice.redirectToHeroEventDetails(),
        //     routeParam: 'eventDetails',
        // },
        {
            dataTest: 'special-sports',
            id: 13,
            label: 'Special Sports',
            onClick: (): void => routerBackoffice.redirectToCmsSpecialSports(),
            routeParam: 'specialSports',
        },
        {
            dataTest: 'quick-link-tray',
            id: 14,
            label: 'Quick Link Tray',
            onClick: (): void => routerBackoffice.redirectToCmsQuickLinkTray(),
            routeParam: 'quickLinkTray',
        },
        {
            dataTest: 'home-page-carousel',
            id: 15,
            label: 'Promo Carousel',
            onClick: (): void => routerBackoffice.redirectToCmsHomePageCarousel(),
            routeParam: 'homePageCarousel',
        },
        {
            dataTest: 'casino-banners',
            id: 16,
            label: 'Casino Banners',
            onClick: (): void => routerBackoffice.redirectToCmsCasinoBanners(),
            routeParam: 'casinoBanners',
        },
        {
            dataTest: 'footer-management',
            id: 17,
            label: 'Footer Management',
            onClick: (): void => routerBackoffice.redirectToCmsFooterManagement(),
            routeParam: 'footerManagement',
        },
        {
            dataTest: 'headline-carousel',
            id: 18,
            label: 'Headline Carousel',
            onClick: (): void => routerBackoffice.redirectToCmsHeadlineCarousel(),
            routeParam: 'headlineCarousel',
        },
        {
            dataTest: 'translations',
            id: 19,
            label: 'Translations',
            onClick: (): void => routerBackoffice.redirectToCmsTranslations(),
            routeParam: 'translations',
        },
        {
            dataTest: 'header-link-panel',
            id: 20,
            label: 'Header Link Panel',
            onClick: (): void => routerBackoffice.redirectToCmsHeaderLinkPanel(),
            routeParam: 'headerLinkPanel',
        },
        {
            dataTest: 'seo-management',
            id: 21,
            label: 'Seo Management',
            onClick: (): void => routerBackoffice.redirectToCmsSeoManagement(),
            routeParam: 'seoManagement',
        },
    ];

    if (topEventsEnable === true) {
        menuItems.push({
            dataTest: 'top-events',
            id: 4,
            label: 'Top Events',
            onClick: (): void => routerBackoffice.redirectToCmsTopEvents(),
            routeParam: 'topEvents',
        });
    }

    if (cmsHiddenTabsLHS.length > 0) {
        return menuItems.filter((item) => !cmsHiddenTabsLHS.includes(item.routeParam));
    }

    return menuItems;
};

export const CmsMenuItems = observer('PromotedEventsList', (): JSX.Element => {
    const { configState, routerBackoffice } = useAppStateContext();
    const cmsParams = routerBackoffice.getParametersString('global', 'tab') ?? 'global';

    const isTabActive = (label: string): boolean => {
        return cmsParams === label;
    };

    const menuItemsForView = renderLhsMenuItems(configState, routerBackoffice);

    const sortedMenuItemsForView = menuItemsForView.sort((a, b) => a.id - b.id);

    return (
        <MenuItemsNavigationCms>
            {sortedMenuItemsForView.map((linkItem) => (
                <MenuButtonWrapperCms
                    data-test={linkItem.dataTest}
                    isActive={isTabActive(linkItem.routeParam)}
                    key={linkItem.routeParam}
                    onClick={linkItem.onClick}
                >
                    {linkItem.label}
                </MenuButtonWrapperCms>
            ))}
        </MenuItemsNavigationCms>
    );
});
